import { batch } from 'react-redux';
import type { AnyAction, ThunkAction } from '@reduxjs/toolkit';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { mergeNeighborhoodMapIntoEntries } from 'src/redux/content/utils/mergeNeighborhoodMapIntoEntries';
import { fetchNeighborhoodMap } from 'src/services/productlistings-api/fetchNeighborhoodMap';
import { fetchTaxonomyPlpContentEntry } from 'src/services/productlistings-api/fetchTaxonomyPlpContentEntry';
import { extractBasePath } from 'src/utils/extractBasePath';
import { selectTaxonomyContentEntry } from 'src/utils/selectTaxonomyContentEntry';
import { actions } from '../slice';

interface SyncTaxonomyContentArgs {
  newPath: string;
  previousPath: string;
}

/**
 * Orchestrates the client-side content update for a shop-PLP URL change:
 * 1. Refetch the neighbourhood map only when the taxonomy base path changed
 *    (a filter-only change keeps the same neighbourhood, so it is skipped).
 * 2. Re-select the matched entry for the new path (against the freshly fetched
 *    map, merged in memory — not yet committed to the store).
 * 3. Fetch that entry's content if it is not already loaded.
 * 4. Commit the map and the content together in a single batched update.
 *
 * The map and content are committed together so there is never an intermediate
 * render where the newly selected entry exists as a content-less stub — that
 * gap is what made the header blank mid-navigation. Merges remain additive, so
 * out-of-order responses are safe.
 *
 * `content.syncing` is flagged for the whole duration so the header can hold its
 * current content until the new content is ready. This is independent of the
 * products request: on a neighbourhood change the sync does extra work (map +
 * content fetch) and can outlast the products fetch, which otherwise left a gap.
 */
export const syncTaxonomyContentFromURL = ({
  newPath,
  previousPath,
}: SyncTaxonomyContentArgs): ThunkAction<
  Promise<void>,
  ReduxApplicationState,
  undefined,
  AnyAction
> => {
  return async (dispatch, getState) => {
    dispatch(actions.setTaxonomyContentSyncing(true));

    try {
      const neighborhoodChanged = extractBasePath(previousPath) !== extractBasePath(newPath);

      const mapEntries = neighborhoodChanged ? await fetchNeighborhoodMap(newPath) : [];

      // Merge the freshly fetched map in memory so selection runs against the
      // up-to-date entries without committing a content-less intermediate state.
      const entries = getState().content.entries ?? [];
      const mergedEntries =
        mapEntries.length > 0 ? mergeNeighborhoodMapIntoEntries(entries, mapEntries) : entries;

      const matched = selectTaxonomyContentEntry(newPath, mergedEntries);

      const content =
        matched && !matched.content ? await fetchTaxonomyPlpContentEntry(matched.url) : null;

      if (mapEntries.length === 0 && !content) {
        return;
      }

      batch(() => {
        if (mapEntries.length > 0) {
          dispatch(actions.mergeNeighborhoodMap(mapEntries));
        }

        if (content) {
          dispatch(actions.mergeEntryContent(content));
        }
      });
    } finally {
      dispatch(actions.setTaxonomyContentSyncing(false));
    }
  };
};
